import React, { useEffect, useMemo, useState } from 'react';
import { SelectableDocument, ProjectDocumentOption } from '../hooks/useRegenerateSelection';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL;

interface UseProjectDocumentOptionsArgs {
  customerId?: string;
  /** Project the report belongs to — its documents sort first */
  projectId?: string;
  /** Document IDs already represented elsewhere in the modal */
  excludeIds: string[];
  /** Only fetch while the modal is open */
  enabled: boolean;
}

/**
 * Loads the customer's existing completed documents so they can be pulled into
 * a regeneration. Documents already in the report (original) or uploaded from
 * the report page (additional) are filtered out.
 *
 * This also covers refresh recovery: documents dropped on the report page
 * before a refresh are plain project documents afterwards, so they show up
 * here rather than being lost with the page state.
 */
export const useProjectDocumentOptions = ({
  customerId,
  projectId,
  excludeIds,
  enabled
}: UseProjectDocumentOptionsArgs) => {
  const [documents, setDocuments] = useState<ProjectDocumentOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stable primitive so the effect doesn't refire on array identity alone
  const excludeKey = useMemo(() => [...excludeIds].sort().join(','), [excludeIds]);

  useEffect(() => {
    if (!enabled || !customerId) return;

    let cancelled = false;
    const token = localStorage.getItem('riskninja-token');
    if (!token) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [docsResponse, projectsResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/customers/${customerId}/documents`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          // Best-effort: only used to label which folder a document lives in
          fetch(`${API_BASE_URL}/api/projects?customerId=${customerId}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => null)
        ]);

        if (!docsResponse.ok) {
          throw new Error(`Failed to load project documents (${docsResponse.status})`);
        }

        const allDocuments = await docsResponse.json();

        const projectNames = new Map<string, string>();
        if (projectsResponse && projectsResponse.ok) {
          try {
            const projects = await projectsResponse.json();
            (Array.isArray(projects) ? projects : []).forEach((p: any) => {
              if (p?.id) projectNames.set(String(p.id), p.name || 'Untitled folder');
            });
          } catch {
            // Labels are cosmetic — ignore a bad projects payload
          }
        }

        const excluded = new Set(excludeKey ? excludeKey.split(',') : []);

        const options: ProjectDocumentOption[] = (Array.isArray(allDocuments) ? allDocuments : [])
          .filter((doc: any) => doc?.status === 'completed' && !excluded.has(String(doc.id)))
          .map((doc: any) => {
            const docProjectId = doc.projectId ? String(doc.projectId) : null;
            return {
              id: String(doc.id),
              name: doc.name || doc.originalName || 'Untitled document',
              size: doc.size,
              policyType: doc.policyType,
              projectId: docProjectId,
              projectName: docProjectId
                ? projectNames.get(docProjectId) || 'Other folder'
                : 'Unfiled',
              sameProject: !!projectId && docProjectId === String(projectId)
            };
          })
          // Same-project documents first, then everything else by name
          .sort((a, b) => {
            if (a.sameProject !== b.sameProject) return a.sameProject ? -1 : 1;
            return a.name.localeCompare(b.name);
          });

        if (!cancelled) setDocuments(options);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load project documents for regeneration:', err);
          setError(err instanceof Error ? err.message : 'Failed to load project documents');
          setDocuments([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [enabled, customerId, projectId, excludeKey]);

  return { documents, loading, error };
};

interface ProjectDocumentPickerProps {
  documents: SelectableDocument[];
  onToggleDocument: (docId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  loading: boolean;
  error?: string | null;
  className?: string;
}

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Collapsible section listing documents that already exist in the customer's
 * projects but aren't part of this report. Collapsed with nothing selected by
 * default — unlike a fresh upload, the user hasn't expressed intent about
 * these yet.
 */
export const ProjectDocumentPicker: React.FC<ProjectDocumentPickerProps> = ({
  documents,
  onToggleDocument,
  onSelectAll,
  onDeselectAll,
  loading,
  error,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [search, setSearch] = useState('');

  const selectedCount = documents.filter(doc => doc.selected).length;

  const visibleDocuments = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return documents;
    return documents.filter(doc => doc.name.toLowerCase().includes(query));
  }, [documents, search]);

  if (!loading && !error && documents.length === 0) {
    return null;
  }

  return (
    <div className={`project-document-picker ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center space-x-2 text-left"
        >
          <svg
            className={`w-3 h-3 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              Other Project Documents
            </h4>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {loading
                ? 'Loading documents from this customer…'
                : `Documents already in this customer's projects (${documents.length} available)`}
            </p>
          </div>
        </button>

        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {selectedCount} selected
          </span>
          {isExpanded && documents.length > 0 && (
            <div className="flex space-x-1">
              {selectedCount < documents.length && (
                <button
                  onClick={onSelectAll}
                  className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                >
                  All
                </button>
              )}
              {selectedCount > 0 && (
                <button
                  onClick={onDeselectAll}
                  className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  None
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>
      )}

      {isExpanded && !loading && documents.length > 0 && (
        <>
          {documents.length > 8 && (
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter documents by name…"
              className="w-full mb-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
            />
          )}

          <div className="space-y-2 max-h-48 overflow-y-auto">
            {visibleDocuments.map((document) => (
              <div
                key={document.id}
                className={`
                  flex items-center p-3 border rounded-lg transition-colors cursor-pointer
                  ${document.selected
                    ? 'border-purple-300 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
                  }
                  hover:border-purple-300 dark:hover:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20
                `}
                onClick={() => onToggleDocument(document.id)}
              >
                <input
                  type="checkbox"
                  checked={document.selected}
                  onChange={() => onToggleDocument(document.id)}
                  className="h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 mr-3"
                  onClick={(e) => e.stopPropagation()}
                />

                <span className="text-lg mr-3" aria-label="File type">📄</span>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                      {document.name}
                    </span>
                    <span className="inline-block text-xs px-2 py-0.5 rounded-full border font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 border-purple-200 dark:border-purple-700 flex-shrink-0">
                      {document.sameProject ? 'This project' : document.projectName || 'Other project'}
                    </span>
                  </div>

                  <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
                    {document.policyType && <span>{document.policyType}</span>}
                    {document.size && <span>{formatFileSize(document.size)}</span>}
                  </div>
                </div>
              </div>
            ))}

            {visibleDocuments.length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
                No documents match “{search}”.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectDocumentPicker;
